type ArticleLawRefs = Record<string, string[]>

const DEFAULT_REFS: ArticleLawRefs = {}

export function useArticleReferences(articleId: Ref<string | null | undefined>) {
  const { loggedIn: isReferenceEnabled } = useUserSession()
  const personalization = usePersonalization('article-law-refs')

  // Local state for optimistic updates to avoid significant delay in UI when user toggles reference items.
  // Updates immediately when user clicks, and then syncs with personalization when it updates.
  const optimisticRefs = ref<ArticleLawRefs>({})
  const hasOptimisticState = ref(false)

  const currentArticleId = computed(() => articleId.value || '')

  function getRefs(): ArticleLawRefs {
    const rawData = personalization.data.value
    if (!rawData || typeof rawData !== 'object') {
      return DEFAULT_REFS
    }

    const normalizedRefs: ArticleLawRefs = {}

    for (const [articleId, nodeIds] of Object.entries(rawData as Record<string, unknown>)) {
      if (typeof articleId !== 'string') {
        continue
      }
      if (typeof nodeIds === 'string') {
        normalizedRefs[articleId] = [nodeIds]
        continue
      }

      if (Array.isArray(nodeIds)) {
        const stringNodeIds = nodeIds.filter((x): x is string => typeof x === 'string')

        if (stringNodeIds.length) {
          const seenNodeIds = new Set<string>()
          const uniqueNodeIds = stringNodeIds.filter((nodeId) => {
            if (seenNodeIds.has(nodeId)) {
              return false
            }
            seenNodeIds.add(nodeId)
            return true
          })

          normalizedRefs[articleId] = uniqueNodeIds
        }
      }
    }

    return normalizedRefs
  }

  const lawReferenceNodeIds = computed<string[]>(() => {
    const id = currentArticleId.value
    if (!id) {
      return []
    }

    if (hasOptimisticState.value) {
      return optimisticRefs.value[id] ?? []
    }
    return getRefs()[id] ?? []
  })

  watch(() => personalization.data.value, () => {
    hasOptimisticState.value = false
  })

  function addLawNodeIdToReferences(nodeId: string) {
    if (!isReferenceEnabled.value || !personalization.isSuccess.value) {
      return
    }
    const id = currentArticleId.value
    if (!id) {
      return
    }

    const allRefs = getRefs()
    const current = allRefs[id] ?? []
    const updated = current.includes(nodeId) ? current : [...current, nodeId]

    optimisticRefs.value = { ...allRefs, [id]: updated }
    hasOptimisticState.value = true

    personalization.set({ ...allRefs, [id]: updated })
  }

  function removeLawIdFromReferences(nodeId: string) {
    if (!isReferenceEnabled.value || !personalization.isSuccess.value) {
      return
    }
    const id = currentArticleId.value
    if (!id) {
      return
    }

    const allRefs = getRefs()
    const filtered = (allRefs[id] ?? []).filter(x => x !== nodeId)
    const updated: ArticleLawRefs = { ...allRefs }
    if (filtered.length) {
      updated[id] = filtered
    }
    else {
      delete updated[id]
    }

    optimisticRefs.value = updated
    hasOptimisticState.value = true

    personalization.set(updated)
  }

  return {
    isReferenceEnabled,
    initialized: computed(() => personalization.isSuccess.value),
    isLoading: computed(() => personalization.isLoading.value),
    lawReferenceNodeIds,
    addLawNodeIdToReferences,
    removeLawIdFromReferences,
  }
}
